import {
  Button,
  FileInput,
  FormGroup,
  Intent,
  ProgressBar,
} from '@blueprintjs/core';
import { FileNameArea, UploaderContainer } from './styles';
import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { WavelightWorkflowState } from '../../views/workflow/types';
import { setVideo, uploadVideo } from '../../views/workflow/reducer';

let interval: any = undefined;

const Uploader = () => {
  const dispatch = useAppDispatch();
  const {
    data,
    isUploading,
    videoUploadCompleted,
    videoUploadError,
    videoUploadErrorMessage,
    estimatedUploadTime,
  }: WavelightWorkflowState = useAppSelector((state) => state.workflow);

  const [running, setRunning] = useState<boolean>(false);
  const [progressValue, setProgressValue] = useState<number>(0);
  const [timer, setTimer] = useState<number>(estimatedUploadTime);

  const handleUploadVideo = useCallback(() => {
    dispatch(uploadVideo());
  }, [dispatch]);

  const handleSelectVideo = useCallback(
    ({ target: { files } }: any) => {
      dispatch(setVideo(files[0]));
      handleUploadVideo();
    },
    [dispatch, handleUploadVideo],
  );

  const getText = useCallback(() => {
    if (isUploading) return 'Carregando:';
    if (videoUploadCompleted) return 'Envio concluído:';
    return 'Arquivo:';
  }, [isUploading, videoUploadCompleted]);

  useEffect(() => {
    if (running) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev > 1) {
            const remainingTime = prev - 1;
            // Atualize a barra de progresso com base no tempo restante
            const progress =
              0.2 +
              0.8 *
                ((estimatedUploadTime - remainingTime) / estimatedUploadTime);
            setProgressValue((prevProgress) =>
              Math.max(prevProgress, progress),
            ); // Garante que o progresso só aumenta
            return remainingTime;
          } else {
            clearInterval(interval);
            return 0;
          }
        });
      }, 1000);
    } else {
      clearInterval(interval);
    }
  }, [running, estimatedUploadTime]);

  useEffect(() => {
    if (progressValue >= 1) setRunning(false);
  }, [progressValue]);

  useEffect(() => {
    if (isUploading) {
      setRunning(true);
      setProgressValue(0.2); // Define o progresso inicial para 20%
    }
  }, [isUploading]);

  useEffect(() => {
    if (videoUploadCompleted) {
      setRunning(false);
      setProgressValue(1);
      setTimer(0); // Oculta o tempo estimado ao concluir
    }
  }, [videoUploadCompleted]);

  useEffect(() => {
    if (videoUploadError) {
      setRunning(false);
      setProgressValue(0);
    }
  }, [videoUploadError]);

  useEffect(() => {
    if (isUploading && estimatedUploadTime > 0) {
      setTimer(estimatedUploadTime);
    }
  }, [isUploading, estimatedUploadTime]);

  const formatFileSize = (size: number | undefined) => {
    if (typeof size !== 'number' || size < 0) {
      return 'Tamanho inválido';
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let unitIndex = 0;
    let fileSize = size;

    while (fileSize >= 1024 && unitIndex < units.length - 1) {
      fileSize /= 1024;
      unitIndex += 1;
    }

    return `${fileSize.toFixed(2)} ${units[unitIndex]}`;
  };

  if (videoUploadError) {
    return (
      <UploaderContainer>
        <strong>Ocorreu um erro ao enviar o vídeo</strong>
        {videoUploadErrorMessage && <span>{videoUploadErrorMessage}</span>}
        <Button intent={Intent.DANGER} fill onClick={handleUploadVideo}>
          Tentar novamente
        </Button>
      </UploaderContainer>
    );
  }

  return (
    <UploaderContainer>
      <FormGroup label="Envio de vídeo">
        <FileInput
          fill
          text="Escolha um arquivo..."
          onInputChange={(e) => handleSelectVideo(e)}
          disabled={isUploading}
          buttonText="Anexar"
        />
      </FormGroup>
      {!!data.video && (
        <>
          <FileNameArea>
            <b>{getText()}</b> {data.video.name}
          </FileNameArea>
          {!videoUploadCompleted && (
            <div>
              <b>Tempo estimado de upload:</b>{' '}
              {timer !== 0 ? `${timer.toFixed(0)} segundos` : 'Calculando...'}
            </div>
          )}
          <div>
            <b>Tamanho do arquivo:</b> {formatFileSize(data.video.size)}
          </div>
        </>
      )}
      {progressValue && progressValue < 1 ? (
        <ProgressBar value={progressValue} />
      ) : null}
    </UploaderContainer>
  );
};

export default Uploader;
